import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Editor, Toolbar } from 'ngx-editor';
import { ToastrService } from 'ngx-toastr';
import { RegExp } from 'src/app/core/config/regex';
import { Milestone, Project, Resource } from 'src/app/core/models/project-pilot.model';
import { Message } from 'src/app/shared/models/domain';
import { CommonService } from 'src/app/shared/services/common.service';
import { ProjectPilotService } from 'src/app/shared/services/project-pilot.service';

@Component({
  selector: 'app-manage-milestone',
  templateUrl: './manage-milestone.component.html',
  styleUrls: ['./manage-milestone.component.scss'],
})
export class ManageMilestoneComponent implements OnInit, OnDestroy {
  @Input() milestone: Milestone;
  @Input() milestones: Milestone[] = [];
  @Output() passEntry: EventEmitter<any> = new EventEmitter();

  public resourceForm: FormGroup;
  public milestoneForm: FormGroup;
  public submitted: boolean = false;
  public resourceSubmitted: boolean = false;
  public selectedFiles: { [key: number]: File } = {};
  private modified: boolean = false;
  public statuses: string[] = [];
  public projects: any[] = [];
  public priorities: string[];
  public isAddingResource: boolean = false;
  public editor: Editor;
  public toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['code', 'blockquote'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['link', 'image'],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];

  constructor(
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private commonService: CommonService,
    private projectPilotService: ProjectPilotService,
    private toaster: ToastrService,
    private datePipe: DatePipe
  ) { }

  ngOnInit(): void {
    this.editor = new Editor();
    this.setMilestoneForm(this.milestone);
    this.projectPilotService.fetchProjects(false).subscribe((projects: Project[]) => {
      this.projects = projects;
    });
    if (this.milestone?.id) {
      this.getResourcesByMilestone(this.milestone.id);
    }
    this.setResources([]);
    this.getStatuses();
    this.priorities = this.commonService.getPriorities();
  }

  ngOnDestroy(): void {
    this.editor.destroy();
  }

  private dateRangeValidator(): ValidatorFn {
    return (formGroup: FormGroup): ValidationErrors | null => {
      const startDate = formGroup.get('startDate')?.value;
      const deadline = formGroup.get('deadline')?.value;

      if (startDate && deadline) {
        const start = new Date(startDate);
        const end = new Date(deadline);

        if (end < start) {
          return { invalidDateRange: true };
        }
      }
      return null;
    };
  }

  public setMilestoneForm(milestone?: Milestone): void {
    const today = this.datePipe.transform(new Date(), 'yyyy-MM-dd');
    if (!milestone) {
      milestone = {
        id: null,
        title: '',
        description: '',
        deadline: '',
        status: 'NOT_STARTED',
        priority: 'MEDIUM',
        estimatedHours: 0,
        actualHours: 0,
        startDate: '',
        notes: '',
        completedDate: '',
        disabled: false,
        projectId: null,
        projectName: '',
      } as Milestone;
    }
    this.milestoneForm = this.fb.group({
      id: this.fb.control(milestone.id),
      title: this.fb.control(milestone.title, [Validators.required]),
      description: this.fb.control(milestone.description, [Validators.maxLength(2000)]),
      deadline: this.fb.control(
        milestone.deadline ? this.datePipe.transform(milestone.deadline, 'yyyy-MM-dd') : today,
        [Validators.required]
      ),
      status: this.fb.control(milestone.status, [Validators.required]),
      priority: this.fb.control(milestone.priority, [Validators.required]),
      estimatedHours: this.fb.control(milestone.estimatedHours, [Validators.min(0)]),
      actualHours: this.fb.control(milestone.actualHours, [Validators.min(0)]),
      startDate: this.fb.control(
        milestone.startDate ? this.datePipe.transform(milestone.startDate, 'yyyy-MM-dd') : today,
        [Validators.required]
      ),
      notes: this.fb.control(milestone.notes, [Validators.maxLength(2000)]),
      completedDate: this.fb.control(this.datePipe.transform(milestone.completedDate, 'yyyy-MM-dd')),
      disabled: this.fb.control(milestone.disabled),
      projectId: this.fb.control(milestone.projectId, [Validators.required]),
      projectName: this.fb.control(milestone.projectName),
    }, { validators: this.dateRangeValidator() });

    this.milestoneForm.valueChanges.subscribe(() => (this.modified = true));
  }

  private getStatuses(): void {
    this.statuses = ['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED', 'ON_HOLD',];
  }

  get f() {
    return this.milestoneForm.controls;
  }

  get r() {
    return this.resourceForm.controls;
  }

  public saveMilestone(inProgress?: any): void {
    this.submitted = true;
    if (this.milestoneForm.valid) {
      const milestone = this.milestoneForm.value as Milestone;
      if (!this.modified) {
        this.activeModal.close(false);
        this.commonService.setToastr(200, `Milestone has been updated successfully!`);
        return;
      }
      inProgress.value = true;
      if (milestone.id) {
        this.projectPilotService.updateMilestone(milestone.id, milestone).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              milestone.projectId = this.f.projectId.value;
              this.passEntry.emit(response);
              this.toaster.success(response.message);
            } else {
              this.commonService.setToastr(500, response.message);
            }
            inProgress.value = false;
            this.submitted = false;
          },
          error: (error: HttpErrorResponse) => {
            this.toaster.error(error?.error?.name || error.message);
            inProgress.value = false;
          },
        });
      } else {
        this.projectPilotService.createMilestone(milestone).subscribe({
          next: (response: Message) => {
            if (!response.failed) {
              const milestoneId = parseInt(response.payload);
              this.f.id.setValue(milestoneId);
              this.milestoneForm.get('id').setValue(milestoneId);
              this.passEntry.emit(response);
              this.toaster.success(response.message);
            } else {
              this.commonService.setToastr(500, response.message);
            }
            inProgress.value = false;
            this.submitted = false;
          },
          error: (error: HttpErrorResponse) => {
            this.toaster.error(error?.error?.name || error.message);
            inProgress.value = false;
          },
        });
      }
    }
  }

  get resources(): FormArray {
    return this.resourceForm.get('resources') as FormArray;
  }

  private setResources(resources: Resource[]): void {
    this.resourceForm = this.fb.group({
      resources: this.fb.array([]),
    });
    if (resources.length > 0) {
      resources.forEach((r) => this.addResource(r));
    }
    this.addResource();
  }

  public addResource(resource?: Resource): void {
    this.isAddingResource = true;
    this.resources.push(
      this.fb.group({
        id: this.fb.control(resource?.id || null),
        fileUrl: this.fb.control(resource?.fileUrl, []),
        label: this.fb.control(resource?.label, [Validators.required]),
        disabled: this.fb.control(resource?.disabled || false),
        milestoneId: this.fb.control(resource?.milestoneId || this.f.id.value),
      })
    );
    this.isAddingResource = false;
    this.resourceSubmitted = false;
  }

  public triggerFileInput(index: number): void {
    const fileInput = document.getElementById(`fileInput-${index}`) as HTMLInputElement;
    fileInput.click();
  }

  public handleFileInput(event: any, index: number): void {
    const file = event.target.files[0];
    if (file) {
      const validTypes = [
        'image/jpeg',
        'image/png',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain'
      ];
      if (!validTypes.includes(file.type)) {
        this.toaster.error('Invalid file type. Allowed: JPG, JPEG, PNG, PDF, DOC, DOCX, TXT');
        return;
      }
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        this.toaster.error('File size exceeds 10MB limit');
        return;
      }
      this.selectedFiles[index] = file;
      this.resources.at(index).patchValue({ fileUrl: file.name });
      const browseButton = document.getElementById(`browseButton-${index}`) as HTMLButtonElement;
      if (browseButton) {
        browseButton.innerHTML = `<i class="bx bx-upload"></i> ${file.name}`;
      }
    }
  }

  public downloadFile(fileUrl: string, index: number): void {
    const a = document.createElement('a');
    a.href = fileUrl;
    a.setAttribute('download', fileUrl.split('/').pop() || 'file');
    a.setAttribute('target', '_self');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  public upsertResource(resource: any, index: number): void {
    this.resourceSubmitted = true;
    resource.markAllAsTouched();
    if (resource.valid) {
      const resourceValue = resource.value as Resource;
      resourceValue.milestoneId = this.f.id.value;
      resource.isUpdateInProgress = true;

      const fileInput = document.getElementById(`fileInput-${index}`) as HTMLInputElement;
      const file = fileInput?.files?.[0];
      const formData = new FormData();
      if (file) {
        formData.append('file', file);
      }

      const showToastAndFinish = (messageText: string) => {
        this.toaster.success(messageText);
        resource.isUpdateInProgress = false;
        this.resourceSubmitted = false;
      };

      const handleFileUpload = (resourceId: number, messageText: string) => {
        if (file) {
          this.projectPilotService.upsertMilestoneFile(resourceId, formData).subscribe({
            next: (fileMessage: Message) => {
              if (!fileMessage.failed && fileMessage.payload) {
                this.resources.at(index).patchValue({ fileUrl: fileMessage.payload });
                showToastAndFinish(messageText);
              } else {
                this.toaster.error(fileMessage.message || 'File already exists!');
                resource.isUpdateInProgress = false;
              }
            },
            error: () => {
              this.toaster.error('File already exists!');
              resource.isUpdateInProgress = false;
            }
          });
        } else {
          showToastAndFinish(messageText);
        }
      };

      if (resourceValue.id) {
        this.projectPilotService.updateResource(resourceValue.id, resourceValue).subscribe({
          next: (message: Message) => {
            if (!message.failed) {
              handleFileUpload(resourceValue.id, message.message);
            } else {
              this.toaster.error(message.message || 'Update failed');
              resource.isUpdateInProgress = false;
            }
          },
          error: () => {
            this.toaster.error('Update request failed');
            resource.isUpdateInProgress = false;
          }
        });
      } else {
        this.projectPilotService.createResource(resourceValue).subscribe({
          next: (message: Message) => {
            if (!message.failed) {
              const newResourceId = parseInt(message.payload);
              this.resources.at(index).patchValue({ id: newResourceId });
              handleFileUpload(newResourceId, message.message);
            } else {
              this.toaster.error(message.message || 'Create failed');
              resource.isUpdateInProgress = false;
            }
          },
          error: () => {
            this.toaster.error('Create request failed');
            resource.isUpdateInProgress = false;
          }
        });
      }
    } else {
      this.toaster.error('Please fill in all required fields for the resource.');
    }
  }

  public deleteResource(resource: any, index: number): void {
    if (resource.valid) {
      const resourceValue = resource.value as Resource;
      resourceValue.milestoneId = this.f.id.value;
      if (resourceValue.id) {
        resource.isDeleteInProgress = true;
        this.projectPilotService.deleteResource(resourceValue.id).subscribe({
          next: (message: Message) => {
            if (!message.failed) {
              this.resources.removeAt(index);
              this.commonService.setToastr(200, message.message);
            }
            resource.isDeleteInProgress = false;
          },
          error: (error: HttpErrorResponse) => {
            resource.isDeleteInProgress = false;
          },
        });
      } else {
        this.resources.removeAt(index);
        if (this.resources.length < 1) {
          this.addResource();
        }
      }
    }
  }

  private getResourcesByMilestone(milestoneId: number): void {
    if (milestoneId) {
      this.projectPilotService.getResources(milestoneId).subscribe({
        next: (resources) => this.setResources(resources),
        error: (error: HttpErrorResponse) => {
        },
      });
    }
  }

  public getPriorityBadgeClass(priority: string): string {
    switch (priority) {
      case 'HIGH':
        return 'badge bg-danger px-3 py-1 text-white rounded-pill';
      case 'MEDIUM':
        return 'badge bg-warning px-3 py-1 text-dark rounded-pill';
      case 'LOW':
        return 'badge bg-info px-3 py-1 text-white rounded-pill';
      default:
        return 'badge bg-secondary px-3 py-1 text-white rounded-pill';
    }
  }

  public getBadgeClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'not_started':
        return 'badge bg-secondary text-white rounded-pill';
      case 'in_progress':
        return 'badge bg-info text-dark rounded-pill';
      case 'completed':
        return 'badge bg-success text-white rounded-pill';
      case 'on_hold':
        return 'badge bg-danger text-white rounded-pill';
      default:
        return 'badge bg-light text-dark rounded-pill';
    }
  }

  public formatStatus(status: string): string {
    return status.replace(/_/g, ' ');
  }
}